/**
 * Browser TTS provider — improved voice picking over the raw OS voices.
 *
 * Uses the Web Speech API (window.speechSynthesis). Free, no API key, but
 * voice quality varies wildly by host. Default OS voices (David/Zira on
 * Windows, eSpeak on Linux) are notoriously robotic. Chromium-based hosts
 * (which includes Electron) expose "Google" voices that sound noticeably
 * better, and modern Windows / macOS expose neural voices in some configs.
 *
 * Voice-picking strategy (auto mode):
 *   1. User-supplied `voiceURI` override always wins
 *   2. Prefer voices with markers indicating neural/high-quality engines:
 *      "Natural", "Online", "Neural", "Premium", "Enhanced"
 *   3. Then prefer Google voices (Chromium's bundled cloud voices)
 *   4. Match the personality's gender hint via name patterns
 *   5. Fall back to any English voice
 *   6. Last resort: the host's default voice
 *
 * Public API mirrors the dispatcher contract:
 *   speak(text, { voice, voiceURI }) → { ok, error? }
 *   stop() → void
 *   listVoices() → SpeechSynthesisVoice[]  (for the Settings dropdown)
 */

const VOICE_PROFILES = Object.freeze({
  mentor:  { rate: 0.95, pitch: 1.00, gender: null },
  analyst: { rate: 1.05, pitch: 0.95, gender: null },
  drill:   { rate: 0.90, pitch: 0.85, gender: 'male' },
  friend:  { rate: 1.00, pitch: 1.10, gender: 'female' },
});

// Markers that indicate a higher-quality voice engine. Order matters —
// "Natural" / "Online" / "Neural" are the strongest signals; "Enhanced"
// is the macOS premium-voice marker.
const QUALITY_MARKERS = ['natural', 'neural', 'online', 'premium', 'enhanced', 'wavenet'];

const MALE_NAME_RX = /\b(david|mark|guy|alex|daniel|fred|tom|christopher|ryan|brian|james|andrew|eric|jacob|matthew|paul|edward|google\sus\senglish|google\suk\senglish\smale)\b/i;
const FEMALE_NAME_RX = /\b(zira|hazel|samantha|karen|moira|tessa|fiona|aria|jenny|jane|sara|amy|emma|olivia|google\suk\senglish\sfemale)\b/i;

export const isAvailable = () => {
  return typeof window !== 'undefined' &&
         typeof window.speechSynthesis !== 'undefined' &&
         typeof window.SpeechSynthesisUtterance !== 'undefined';
};

/**
 * List voices for the Settings dropdown. Returns a normalized shape so
 * the UI doesn't need to know about SpeechSynthesisVoice details.
 */
export const listVoices = () => {
  if (!isAvailable()) return [];
  const voices = window.speechSynthesis.getVoices() || [];
  return voices.map(v => ({
    voiceURI: v.voiceURI,
    name: v.name,
    lang: v.lang,
    localService: v.localService,
    default: v.default,
    // Heuristic quality label for the dropdown — helps the user pick.
    qualityHint: QUALITY_MARKERS.some(m => v.name.toLowerCase().includes(m))
      ? 'High'
      : (/^google/i.test(v.name) ? 'Good' : 'Standard'),
  }));
};

const pickAutoVoice = (gender) => {
  if (!isAvailable()) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (voices.length === 0) return null;

  // English-only pool first; fall back to all voices if nothing English available
  const enPool = voices.filter(v => /^en/i.test(v.lang || ''));
  const pool = enPool.length > 0 ? enPool : voices;

  // Score each voice. Higher = better. We tiebreak by sort.
  const score = (v) => {
    const name = (v.name || '').toLowerCase();
    let s = 0;
    // Quality markers — neural/online/natural voices are dramatically better
    for (const m of QUALITY_MARKERS) {
      if (name.includes(m)) { s += 100; break; }
    }
    // Google voices in Chromium are usually mid-tier-good
    if (/^google/i.test(v.name)) s += 50;
    // Gender match adds a bonus when the personality requests one
    if (gender === 'male' && MALE_NAME_RX.test(v.name)) s += 30;
    if (gender === 'female' && FEMALE_NAME_RX.test(v.name)) s += 30;
    // Non-default voices that are network-based tend to be cloud (better)
    if (!v.localService) s += 10;
    // Default voice is the host's preference — small bonus as a fallback
    if (v.default) s += 5;
    return s;
  };

  const ranked = [...pool].sort((a, b) => score(b) - score(a));
  return ranked[0] || null;
};

const findByURI = (voiceURI) => {
  if (!voiceURI || !isAvailable()) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  return voices.find(v => v.voiceURI === voiceURI) || null;
};

/**
 * Speak text. Returns a Promise so the dispatcher can await completion
 * uniformly across providers (cloud providers are async by nature).
 */
export const speak = (text, options = {}) => {
  return new Promise((resolve) => {
    if (!isAvailable()) {
      resolve({ ok: false, error: 'SpeechSynthesis not available' });
      return;
    }
    if (typeof text !== 'string' || text.trim().length === 0) {
      resolve({ ok: false, error: 'Empty text' });
      return;
    }
    const voiceId = options.voice && VOICE_PROFILES[options.voice] ? options.voice : 'mentor';
    const profile = VOICE_PROFILES[voiceId];

    try {
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = profile.rate;
      utter.pitch = profile.pitch;
      // Manual override → exact URI match. Else auto-pick by quality + gender.
      const matched = options.voiceURI
        ? (findByURI(options.voiceURI) || pickAutoVoice(profile.gender))
        : pickAutoVoice(profile.gender);
      if (matched) utter.voice = matched;
      utter.onstart = () => {
        if (typeof options.onStart === 'function') options.onStart();
      };
      utter.onend = () => {
        if (typeof options.onEnd === 'function') options.onEnd();
        resolve({ ok: true });
      };
      utter.onerror = (e) => {
        if (typeof options.onEnd === 'function') options.onEnd();
        resolve({ ok: false, error: e?.error || 'utterance error' });
      };
      window.speechSynthesis.speak(utter);
    } catch (e) {
      resolve({ ok: false, error: e?.message || String(e) });
    }
  });
};

export const stop = () => {
  if (!isAvailable()) return;
  try { window.speechSynthesis.cancel(); } catch { /* */ }
};

export { VOICE_PROFILES };
